import {
  createPaginationStore,
  DataTablePagination,
} from "@/components/zthd/data-table-pagination-mysql";
import { format } from "date-fns";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { fetcher, swrKey } from "@/lib/swr";
import { MyEngagementStatus } from "@/components/engagements/my-completion";
import { CompletionProgress } from "@/components/engagements/completion-progress";
import { TagPill } from "@/components/prds/tag-pill";
import { Chip } from "@/components/badge/chip";
import IssueTypeChip from "@/components/prds/issue-type-chip";
import { RowCard } from "@/components/list/row-card";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { RefreshButton } from "@/components/list/refresh-button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MOBILE_SELECT_MIN, TabSelect } from "@/components/nav/route-tabs";
import CreatePrd from "./Create";
import { PrdToolbar } from "./prd-toolbar";
import { AddAppDropdown } from "./schedule-board";
import DocNameCell from "@/components/prds/doc-name-cell";
import { FollowButton } from "@/components/prds/follow-button";
import OwnerCell from "@/components/prds/owner-cell";
import {
  PRD_LIST_UI_PARAMS,
  PRD_STAGE_PARAM,
  PRD_STATUS_PARAM,
} from "@/constants/prd-search-params";
import { STATE_ONLINE, stateInfo } from "@/constants/version-state";
import { useApps } from "@/hooks/apps";
import { useTags } from "@/hooks/tags";
import { engagementState } from "@/lib/engagement";
import { prdDetailPath } from "@/lib/paths";
import { cn } from "@/lib/utils";
import type { Prd } from "@/types/domain";
import { DATE_SHORT } from "@/lib/date";
import { CopyShortId } from "@/components/prds/copy-short-id";
import { PrLinks } from "@/components/prds/pr-links";
import { ExecStageChip } from "@/components/prds/exec-stage-chip";
import { AgentSessionChip } from "@/components/prds/agent-session-chip";

// One persisted pagination store per table instance that can share a page:
// "prd" is the main table (/prds, app lists, My PRDs), "prd-participating"
// the Participating table mounted beside it on home / the user page.
const PAGINATION_STORES = {
  prd: createPaginationStore(0, 25, "prd"),
  "prd-participating": createPaginationStore(0, 25, "prd-participating"),
};
export type PrdTableStoreKey = keyof typeof PAGINATION_STORES;

// Each list row carries one App relationship and its version stage.
const AppTags = ({
  prd,
  stageFilter,
}: {
  prd: Prd;
  /** Active stage-tab key: rows only chip the relations of that stage —
      the rest is noise here, the detail page has the full picture. */
  stageFilter?: string;
}) => {
  const apps = useApps();
  const appName = (id: string) =>
    apps.find((a) => String(a.id) === id)?.name ?? id;
  // One chip per relation: scheduled ones carry the version state
  // (denormalized on the engagement), pool rows read 未排期.
  const wish = (prd?.engagements ?? []).map((e) => ({
    id: String(e.appId ?? ""),
    name: appName(String(e.appId ?? "")),
    state: e.versionId ? engagementState(e) : undefined,
  }));
  // Legacy documents without App links offer an App picker on the No apps tab.
  if (wish.length === 0)
    return stageFilter === "none" ? (
      <AddAppDropdown prd={prd} />
    ) : (
      <Chip tone="red">No apps</Chip>
    );

  // Three tones, by what the glance needs to answer: red (destructive) =
  // needs attention (未排期 / 下线 / 废弃 / No apps), primary = bound and
  // alive (规划中→线上, the active pipeline), neutral = settled history
  // (被拒/已归档). The state text is detail for a closer look; full
  // per-state colors made rows unscannable.
  // Multiple relations stack vertically in this column (one per line, right
  // edge against the owner column) instead of widening the row.
  const ACTIVE_MAX_STATE = STATE_ONLINE; // beyond it the relation is history
  const DEAD_STATES = new Set([271, 280]); // 下线/废弃 — destructive

  // With a specific stage tab active, only that stage's relations show
  // ("pool" = unscheduled ones, a state code = versions at that state);
  // "all"/"done" keep everything. Guarded fallback to the full set — the
  // bucket definition should guarantee at least one match.
  const visible = (() => {
    if (!stageFilter || stageFilter === "all" || stageFilter === "done")
      return wish;
    const matches =
      stageFilter === "pool"
        ? wish.filter((w) => w.state === undefined)
        : wish.filter((w) => w.state === Number(stageFilter));
    return matches.length > 0 ? matches : wish;
  })();

  return (
    <div
      // Hidden on phones (owner 2026-09-12): the title keeps its room, the
      // app/state context lives on the detail page.
      className={cn(
        "hidden shrink-0 items-center gap-1.5 md:flex",
        visible.length > 1 && "flex-col items-end gap-1"
      )}
    >
      {visible.map((w) => {
        const s = w.state !== undefined ? stateInfo(w.state) : undefined;
        const tone =
          w.state === undefined
            ? "red"
            : w.state <= ACTIVE_MAX_STATE
              ? "primary"
              : DEAD_STATES.has(w.state)
                ? "red"
                : "neutral";
        return (
          <Chip key={w.id} tone={tone}>
            {w.name} · {w.state !== undefined ? (s?.label ?? w.state) : "未排期"}
          </Chip>
        );
      })}
    </div>
  );
};

// Labels on the PRD (associations to the global tag vocabulary), shown as Linear-style pills before the doc name; clicking filters by that tag.
const TagLinks = ({ prd }: { prd: Prd }) => {
  const allTags = useTags();
  const tags = (prd?.tagIds ?? [])
    .map((id) => allTags.find((t) => t.id === id))
    .filter((t): t is (typeof allTags)[number] => !!t);
  if (tags.length === 0) return null;

  // Hidden on phones: labels are secondary — the title keeps its room.
  return (
    <div className="hidden shrink-0 flex-wrap items-center gap-1.5 lg:flex">
      {tags.map((t) => (
        <TagPill key={t.id} tag={t.name} color={t.color} to={`/tags/${t.id}`} />
      ))}
    </div>
  );
};

const PrdCard = ({
  prd,
  displayIndex,
  breadcrumb,
  showPersonalStatus,
  stageFilter,
  hideLifecycle,
}: {
  prd: Prd;
  displayIndex: number;
  breadcrumb?: { label?: string; to?: string }[];
  showPersonalStatus?: boolean;
  stageFilter?: string;
  /** Docs view hides per-app scheduling and personal completion controls. */
  hideLifecycle?: boolean;
}) => {
  const navigate = useNavigate();

  return (
    <RowCard
      onClick={() => navigate(prdDetailPath(prd.id, prd.engagementId), { state: { breadcrumb } })}
      // Zebra rows: taller multi-relation cards need a boundary cue.
      className={displayIndex % 2 === 0 ? "bg-muted/40" : undefined}
    >
      {/* Phones keep only the essentials (short ID, type, title, status,
          owner): completion, App/version chips and row actions hide below
          md — the detail page has them all. */}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {/* Leading completion circle, as on App Prds / Inbox rows. */}
        {prd.engagements?.[0] && (
          <div className="hidden md:contents">
            <CompletionProgress engagement={prd.engagements[0]} />
          </div>
        )}
        <CopyShortId shortId={prd.shortId} />
        <IssueTypeChip prd={prd} />
        <DocNameCell prd={prd} />
        <PrLinks prd={prd} />
        {!hideLifecycle && prd.myCompletedAt && <Chip>My Done</Chip>}
        {!hideLifecycle && !prd.myCompletedAt && prd.myStartedAt && <Chip tone="yellow">In Process</Chip>}
      </div>
      <TagLinks prd={prd} />
      {!hideLifecycle && (
        <div className="hidden md:contents">
          <AppTags prd={prd} stageFilter={stageFilter} />
        </div>
      )}
      <div className="flex shrink-0 items-center gap-3">
        <AgentSessionChip prd={prd} fixed />
        <ExecStageChip prd={prd} fixed />
        <OwnerCell prd={prd} />
        {prd.createdAt && (
          <span className="hidden shrink-0 text-xs tabular-nums text-muted-foreground md:block">
            {format(new Date(prd.createdAt), DATE_SHORT)}
          </span>
        )}
        {/* Row actions only on the dashboard work queue. Browse lists
            (/prds, tag pages) are pure retrieval — engagement/lifecycle
            actions live on the detail page and the owner's queue. */}
        {showPersonalStatus && (
          <div className="hidden md:contents">
            {/* RowCard's inner-control guard keeps these clicks from
                navigating. */}
            <FollowButton prd={prd} compact />
            {prd.engagements?.[0] && <MyEngagementStatus engagement={prd.engagements[0]} prdId={prd.id} issueType={prd.type} />}
          </div>
        )}
      </div>
    </RowCard>
  );
};

// Each row has one engagement, so its version directly determines the stage.
const stageOf = (prd: Prd): { key: string; order: number; label: string } => {
  const e = prd.engagements?.[0];
  if (!e) return { key: "none", order: -2, label: "No apps" };
  if (!e.versionId) return { key: "pool", order: -1, label: "未排期" };
  const state = engagementState(e);
  return { key: String(state), order: state, label: stateInfo(state)?.label ?? String(state) };
};

// The full stage-tab roster, lifecycle order. Fixed (not derived from data)
// so every pipeline step is always visible, 0-count included.
const STAGE_TABS = [
  { key: "none", label: "No apps" },
  { key: "pool", label: "未排期" },
  ...[0, 100, 150, 170, 180, 190, 200, 250].map((code) => ({
    key: String(code),
    label: stateInfo(code)?.label ?? String(code),
  })),
];

// Stage is derived from engagements client-side, so stage tabs can't be a
// server query: the open work queue fetches ALL rows (page loop under the
// server's 100-per-page cap) and filters/paginates locally. key[0] stays
// "/prds" so mutateRequest("/prds") after personal completion refreshes this too.
const fetchAllOpenPrds = async ([endpoint, params]: [
  string,
  Record<string, unknown>,
  ...unknown[],
]): Promise<Prd[]> => {
  const pageSize = 100;
  const all: Prd[] = [];
  for (let pageIndex = 0; ; pageIndex++) {
    const res = await fetcher<{ total?: number; data?: Prd[] }>([
      endpoint,
      { ...params, completed: false, pageIndex, pageSize },
    ]);
    const rows = res?.data ?? [];
    all.push(...rows);
    if (rows.length < pageSize || all.length >= (res?.total ?? 0)) break;
  }
  return all;
};

const PrdTable = ({
  params,
  hideCreate,
  breadcrumb,
  workQueue,
  statusParam = PRD_STATUS_PARAM,
  storeKey = "prd",
}: {
  /** Extra query params for /prds; appId also drives the UI. */
  params?: { appId?: string; [key: string]: unknown };
  hideCreate?: boolean;
  breadcrumb?: { label?: string; to?: string }[];
  /** Search param holding the All/My Open/My Done tab — a second table on the same
      page passes its own so the tabs don't flip together. */
  statusParam?: string;
  /** Pagination store — a second table on the same page passes its own so
      paging doesn't leak between them. */
  storeKey?: PrdTableStoreKey;
  /** Personal-queue mode (dashboard "/"): All/My Open/My Done sub-tabs, open rows
      grouped by stage, personal completion and reminder controls instead of the ⋯ menu. */
  workQueue?: boolean;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsObj = Object.fromEntries(searchParams.entries());
  const usePaginationStore = PAGINATION_STORES[storeKey];
  const paginationStore = usePaginationStore();
  const { pageIndex, pageSize } = paginationStore;
  const setPageIndex = usePaginationStore((state) => state.setPageIndex);

  // Browse all work by default; the personal queue defaults to unfinished work.
  const selectedStatus = searchParams.get(statusParam);
  const status = selectedStatus === "done" || selectedStatus === "open" || selectedStatus === "all"
    ? selectedStatus : workQueue ? "open" : "all";
  const completionFilter = status === "all" ? {} : { completed: status === "done" };
  const workQueueOpen = workQueue && status === "open";

  // Tab-control params (status / stage / participating tabs) are UI state —
  // keep them out of the API params. Add the personal completed filter separately
  // so a sibling table's tab switch does not change this key.
  const restSearch = { ...searchParamsObj };
  for (const key of PRD_LIST_UI_PARAMS) delete restSearch[key];

  // Server-paginated list — every view except the open work queue.
  const { data, isLoading, isValidating, mutate } = useSWR<{
    data: Prd[];
    total: number;
  }>(
    workQueueOpen
      ? null
      : swrKey("/prds", { ...restSearch, ...params, ...completionFilter, pageIndex, pageSize }),
    { keepPreviousData: true }
  );

  // Open work queue: all rows at once for client-side stage tabs.
  const {
    data: allOpen,
    isLoading: allLoading,
    isValidating: allValidating,
    mutate: mutateAll,
  } = useSWR<Prd[]>(
    workQueueOpen ? [...swrKey("/prds", { ...restSearch, ...params }), "all-open"] : null,
    fetchAllOpenPrds,
    { keepPreviousData: true }
  );

  // Tab-label counts: cheap pageSize-1 probes. On the dashboard the open key
  // is identical to MyPrds' issue-count fetch, so SWR dedupes the pair.
  const { data: openData } = useSWR<{ data: Prd[]; total: number }>(
    swrKey("/prds", { ...restSearch, ...params, completed: false, pageIndex: 0, pageSize: 1 })
  );
  const { data: doneData } = useSWR<{ data: Prd[]; total: number }>(
    swrKey("/prds", { ...restSearch, ...params, completed: true, pageIndex: 0, pageSize: 1 })
  );
  const count = (n?: number) => (n === undefined ? "" : ` (${n})`);

  useEffect(() => {
    setPageIndex(0);
  }, [searchParams, setPageIndex]);

  const setStatus = (value: string) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(statusParam, value);
      // A stage pick doesn't carry over to the done tab's flat list (the
      // stage tab belongs to the work queue only).
      if (workQueue) next.delete(PRD_STAGE_PARAM);
      return next;
    });
  const setStage = (value: string) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === "all") next.delete(PRD_STAGE_PARAM);
      else next.set(PRD_STAGE_PARAM, value);
      return next;
    });

  // Stage tabs (open work queue): the fixed roster with live counts.
  const open = allOpen ?? [];
  const stages = (() => {
    if (!workQueueOpen) return [];
    const countByKey = new Map<string, number>();
    for (const prd of open) {
      const { key } = stageOf(prd);
      countByKey.set(key, (countByKey.get(key) ?? 0) + 1);
    }
    return STAGE_TABS.map((t) => ({ ...t, count: countByKey.get(t.key) ?? 0 }));
  })();
  const stageParam = searchParams.get(PRD_STAGE_PARAM) ?? "all";
  const activeStage = stages.some((s) => s.key === stageParam)
    ? stageParam
    : "all";
  const filteredOpen =
    activeStage === "all"
      ? open
      : open.filter((prd) => stageOf(prd).key === activeStage);

  const total = workQueueOpen ? filteredOpen.length : (data?.total ?? 0);
  const prds: Prd[] = workQueueOpen
    ? filteredOpen.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize)
    : (data?.data ?? []);
  // keepPreviousData: tab / page / filter switches keep the old rows on screen
  // and only spin the refresh button; the text loader is for the very first load.
  const loading = workQueueOpen ? allLoading : isLoading;
  const initialLoading = workQueueOpen ? loading && !allOpen : loading && !data;
  const validating = workQueueOpen ? allValidating : isValidating;
  const refresh = () => (workQueueOpen ? mutateAll() : mutate());

  return (
    <div className="flex flex-col gap-4">
      <div className="flex w-full flex-wrap items-center gap-2">
        {/* Segmented shadcn Tabs (not the underline route-tab bar): a compact
            in-place mode switch that shares the toolbar row. Replaces the old
            shared status toggle on every PRD list. */}
        {(<Tabs value={status} onValueChange={setStatus}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="open">
                My Open{count(openData?.total)}
              </TabsTrigger>
              <TabsTrigger value="done">
                My Done{count(doneData?.total)}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        <PrdToolbar />
        <div className="flex space-x-2 ml-auto">
          {!hideCreate && <CreatePrd appId={params?.appId} />}
          <RefreshButton spinning={validating} onClick={refresh} />
        </div>
      </div>
      {/* Stage filter for the open queue — same segmented style, one level
          below All/My Open/My Done. "All" first, then only the buckets present.
          Narrow screens scroll the strip: v4 TabsList height comes from a
          data-attribute selector, so h-auto + flex-wrap can't take effect
          (wrapped triggers would overflow onto the rows). */}
      {workQueueOpen && stages.length > 0 && (
        <Tabs value={activeStage} onValueChange={setStage}>
          {/* Phones: the 8-bucket strip collapses into one Select. */}
          {stages.length + 1 >= MOBILE_SELECT_MIN && (
            <div className="md:hidden">
              <TabSelect
                options={[
                  { value: "all", label: `All (${open.length})` },
                  ...stages.map((s) => ({
                    value: s.key,
                    label: `${s.label} (${s.count})`,
                  })),
                ]}
                value={activeStage}
                onValueChange={setStage}
              />
            </div>
          )}
          <TabsList
            className={cn(
              "max-w-full justify-start overflow-x-auto",
              stages.length + 1 >= MOBILE_SELECT_MIN && "hidden md:inline-flex"
            )}
          >
            <TabsTrigger value="all">
              All ({open.length})
            </TabsTrigger>
            {stages.map((s) => (
              <TabsTrigger key={s.key} value={s.key}>
                {s.label} ({s.count})
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}
      {initialLoading && <ListLoading>Loading PRDs…</ListLoading>}
      {!loading && prds.length === 0 && <ListEmpty>No results.</ListEmpty>}
      <div className="flex flex-col gap-1">
        {prds.map((prd, idx) => (
          <PrdCard
            key={prd.engagementId ?? prd.id}
            prd={prd}
            displayIndex={pageIndex * pageSize + idx + 1}
            breadcrumb={breadcrumb}
            showPersonalStatus={workQueue}
            stageFilter={workQueueOpen ? activeStage : undefined}
            hideLifecycle={false}
          />
        ))}
      </div>
      <DataTablePagination total={total} store={paginationStore} />
    </div>
  );
};

export default PrdTable;
